import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Award, Search, Check, X, CalendarClock } from 'lucide-react';
import { Enrollment, User, Course } from '../../types';
import Card, { CardBody } from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import ExpiryCell from '../../components/common/ExpiryCell';
import ExtendExpiryModal from '../../components/common/ExtendExpiryModal';
import api from '../../services/api';

interface EnrollmentWithDetails extends Enrollment {
  user?: User;
}

export default function ManageEnrollments() {
  const { t } = useTranslation('admin');
  const [enrollments, setEnrollments] = useState<EnrollmentWithDetails[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    userId: '',
    courseId: '',
  });
  const [expiryTarget, setExpiryTarget] = useState<EnrollmentWithDetails | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
    confirmText?: string;
  } | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [enrollmentsRes, usersRes, coursesRes] = await Promise.all([
        api.get('/admin/enrollments'),
        api.get('/admin/users'),
        api.get('/admin/courses'),
      ]);
      setEnrollments(enrollmentsRes.data);
      setUsers(usersRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post('/admin/enrollments', enrollForm);
      fetchData();
      setIsModalOpen(false);
      setEnrollForm({ userId: '', courseId: '' });
    } catch (error) {
      console.error('Failed to create enrollment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = (enrollmentId: string, status: string, label: string) => {
    setConfirmModal({
      isOpen: true,
      title: t('enrollments.confirmEnrollmentTitle', { action: label }),
      message: t('enrollments.confirmEnrollmentMessage', { action: label.toLowerCase() }),
      variant: status === 'active' ? 'info' : 'danger',
      confirmText: label,
      onConfirm: async () => {
        try {
          await api.put(`/admin/enrollments/${enrollmentId}`, { status });
          fetchData();
        } catch (error) {
          console.error(`Failed to ${label.toLowerCase()} enrollment:`, error);
        }
        setConfirmModal(null);
      },
    });
  };

  const handleSetExpiry = async (expiresAt: string | null) => {
    if (!expiryTarget) return;
    await api.put(`/admin/enrollments/${expiryTarget._id}/expiry`, { expiresAt: expiresAt ?? '' });
    await fetchData();
  };

  const handleIssueCertificate = (enrollmentId: string) => {
    setConfirmModal({
      isOpen: true,
      title: t('enrollments.issueCertificateTitle'),
      message: t('enrollments.issueCertificateMessage'),
      variant: 'info',
      confirmText: t('enrollments.issueCertificate'),
      onConfirm: async () => {
        try {
          await api.post(`/admin/certificates/${enrollmentId}`);
          fetchData();
        } catch (error) {
          console.error('Failed to issue certificate:', error);
        }
        setConfirmModal(null);
      },
    });
  };

  const filteredEnrollments = enrollments
    .filter((enrollment) => {
      const userName = enrollment.user?.name?.toLowerCase() || '';
      const userEmail = enrollment.user?.email?.toLowerCase() || '';
      const courseName = enrollment.course?.title?.toLowerCase() || '';
      const search = searchTerm.toLowerCase();
      return userName.includes(search) || userEmail.includes(search) || courseName.includes(search);
    })
    .sort((a, b) => {
      const at = a.enrolledAt ? new Date(a.enrolledAt).getTime() : 0;
      const bt = b.enrolledAt ? new Date(b.enrolledAt).getTime() : 0;
      return bt - at;
    });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('enrollments.title')}</h1>
          <p className="text-gray-500">{t('enrollments.subtitle')}</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <UserPlus className="w-5 h-5 mr-2" />
          {t('enrollments.enrollUser')}
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder={t('enrollments.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredEnrollments.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('enrollments.noEnrollments')}</h3>
              <p className="text-gray-500 mb-4">{t('enrollments.noEnrollmentsDescription')}</p>
              <Button onClick={() => setIsModalOpen(true)}>
                <UserPlus className="w-5 h-5 mr-2" />
                {t('enrollments.enrollUser')}
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card className="overflow-visible">
          <div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">{t('enrollments.tableUser')}</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">{t('enrollments.tableCourse')}</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">{t('enrollments.tableProgress')}</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">{t('enrollments.tableStatus')}</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">{t('enrollments.tableEnrolled')}</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">{t('enrollments.tableAccess')}</th>
                  <th className="text-right py-4 px-6 text-sm font-medium text-gray-500">{t('enrollments.tableActions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnrollments.map((enrollment, index) => (
                  <tr key={enrollment._id || `enrollment-${index}`} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900">{enrollment.user?.name}</p>
                        <p className="text-sm text-gray-500">{enrollment.user?.email}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-600">{enrollment.course?.title}</td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full">
                          <div
                            className={`h-full rounded-full ${
                              enrollment.progress === 100 ? 'bg-green-500' : 'bg-primary-600'
                            }`}
                            style={{ width: `${enrollment.progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{enrollment.progress}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          enrollment.status === 'completed'
                            ? 'bg-green-50 text-green-700'
                            : enrollment.status === 'active'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {enrollment.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-600">
                      {new Date(enrollment.enrolledAt).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-6">
                      <ExpiryCell expiresAt={enrollment.expiresAt} isExpired={enrollment.isExpired} />
                    </td>
                    <td className="py-4 px-6 text-right space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setExpiryTarget(enrollment)}
                      >
                        <CalendarClock className="w-4 h-4 mr-1" />
                        {t('enrollments.extendAccess')}
                      </Button>
                      {enrollment.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleUpdateStatus(enrollment._id, 'active', t('enrollments.approveEnrollment'))}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            {t('enrollments.approveEnrollment')}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleUpdateStatus(enrollment._id, 'rejected', t('enrollments.rejectEnrollment'))}
                          >
                            <X className="w-4 h-4 mr-1" />
                            {t('enrollments.rejectEnrollment')}
                          </Button>
                        </>
                      )}
                      {enrollment.status === 'completed' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleIssueCertificate(enrollment._id)}
                        >
                          <Award className="w-4 h-4 mr-1" />
                          {t('enrollments.issueCertificate')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Enroll Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('enrollments.enrollModalTitle')}
      >
        <form onSubmit={handleEnroll} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('common.user')}</label>
            <select
              value={enrollForm.userId}
              onChange={(e) => setEnrollForm({ ...enrollForm, userId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">{t('enrollments.selectUser')}</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('common.course')}</label>
            <select
              value={enrollForm.courseId}
              onChange={(e) => setEnrollForm({ ...enrollForm, courseId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">{t('enrollments.selectCourse')}</option>
              {courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {t('enrollments.enrollUser')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Extend access expiry */}
      <ExtendExpiryModal
        isOpen={!!expiryTarget}
        onClose={() => setExpiryTarget(null)}
        subject={expiryTarget ? `${expiryTarget.user?.name || ''} · ${expiryTarget.course?.title || ''}` : undefined}
        currentExpiresAt={expiryTarget?.expiresAt}
        onSubmit={handleSetExpiry}
      />

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          confirmText={confirmModal.confirmText || t('common.confirm')}
        />
      )}
    </div>
  );
}
